import {css, StyleSheet} from 'aphrodite';
import assert from 'assert';
import classNames from 'classnames';
import React, {useMemo} from 'react';

import {
  doBoxSchemasWithRegistryQuery,
  doContainerSchemasWithRegistryQuery,
  doLocationSchemasWithRegistryQuery,
  doPlateSchemasWithRegistryQuery,
} from 'inventory/useStorableSchemasQuery';
import {InferredLookupConfigType} from 'lookup-config/inferTypeFromLookupConfig';
import {doAllProcessTaskSchemasForSchemaConfigQuery} from 'processes/modals/utils/ProcessesAllSchemasQueryUtils';
import {doAllResultSchemasQuery} from 'results/useAllResultSchemasQuery';
import {Schema} from 'schemas/SchemaConstants';
import {doAllSchemaInterfacesQuery} from 'schemas/util/useAllSchemaInterfacesQuery';
import {doTagSchemasWithRegistryQuery} from 'schemas/util/useTagSchemasQuery';
import Chip, {ChipSize} from 'ui-library/chips/Chip';
import {BenchlingIconName} from 'ui-library/icons/BenchlingIcon';
import runModal from 'ui-library/runModal';
import {withLoadingScreen} from 'util/Promises';

import {LookupInfosDescription} from './LookupConfigDescription';
import LookupConfigModal, {BasePaneLookupObjectValidation} from './LookupConfigModal';
import {LookupConfigModalContextInfo} from './LookupConfigModalConstants';
import {
  DEFAULT_FINAL_LOOKUP_STEP_TYPES,
  DEFAULT_INTERMEDIATE_LOOKUP_STEP_TYPES,
  LookupConfigSpec,
  LookupStepInfo,
  LookupStepType,
} from './LookupConfigModalConstants';
import {LOOKUP_CHIP} from './LookupConfigStyling';
import {getIconForLookupStepInfo, getStepDisplayInfo, RunLookupConfigModalProps} from './LookupConfigUtils';
import {isLookupConfigValidForModal} from './LookupStepInitializerUtils';

export const EDIT_ACTION_TOOLTIP_TEXT = 'Edit lookup value';

interface RunLookupConfigModalForLookupConfigSummaryChipProps {
  baseSchema?: Schema | null;
  initialLookupConfig: LookupConfigSpec | null;
  firstLookupStepTypes?: Array<LookupStepType>;
  intermediateLookupStepTypes?: Array<LookupStepType>;
  finalLookupStepTypes?: Array<LookupStepType>;
  onSubmit?: (lookupConfig: LookupConfigSpec) => void;
  getLookupConfigValidationError?: (inferredType: InferredLookupConfigType) => string | null;
  basePaneLookupObjectValidation?: BasePaneLookupObjectValidation;
  lookupConfigContext?: LookupConfigModalContextInfo;
}

async function runLookupConfigModalForLookupConfigSummaryChip({
  baseSchema,
  initialLookupConfig,
  onSubmit,
  getLookupConfigValidationError,
  basePaneLookupObjectValidation,
  lookupConfigContext,
  firstLookupStepTypes = [LookupStepType.SCHEMA_FIELD],
  intermediateLookupStepTypes = DEFAULT_INTERMEDIATE_LOOKUP_STEP_TYPES,
  finalLookupStepTypes = DEFAULT_FINAL_LOOKUP_STEP_TYPES,
}: RunLookupConfigModalForLookupConfigSummaryChipProps): Promise<void> {
  const runLookupConfigModalParams: RunLookupConfigModalProps = {
    baseSchema,
    initialLookupConfig,
    firstLookupStepTypes,
    intermediateLookupStepTypes,
    finalLookupStepTypes,
    onRequestSubmit: ({lookupConfig}: {lookupConfig: LookupConfigSpec}) => {
      onSubmit?.(lookupConfig);
    },
    submitButtonText: 'Done',
    basePaneLookupObjectValidation,
    getLookupConfigValidationError,
    context: lookupConfigContext,
  };

  const supportingData = Promise.all([
    doAllResultSchemasQuery(),
    doBoxSchemasWithRegistryQuery(),
    doContainerSchemasWithRegistryQuery(),
    doLocationSchemasWithRegistryQuery(),
    doPlateSchemasWithRegistryQuery(),
    doTagSchemasWithRegistryQuery(),
    doAllSchemaInterfacesQuery(),
    doAllProcessTaskSchemasForSchemaConfigQuery(),
  ]);
  // Ensure all schemas are loaded
  await withLoadingScreen(supportingData);
  assert(
    isLookupConfigValidForModal(runLookupConfigModalParams),
    'Lookup config is not supported in the lookup modal'
  );
  runModal(LookupConfigModal, runLookupConfigModalParams);
}

interface BaseLookupChipProps {
  lookupStepInfos: Array<LookupStepInfo>;
  initialLookupConfig: LookupConfigSpec | null;
  baseSchema?: Schema;
  firstLookupStepTypes?: Array<LookupStepType>;
  intermediateLookupStepTypes?: Array<LookupStepType>;
  finalLookupStepTypes?: Array<LookupStepType>;
  basePaneLookupObjectValidation?: BasePaneLookupObjectValidation;
  chipSize: ChipSize;
  customWidthPx?: number;
}

interface LookupChipPropsStatic {
  isEditable: false;
  lookupModalSchemaFilterFieldFn?: never;
}

interface LookupChipPropsEditable {
  isEditable: true;
  lookupConfigContext?: LookupConfigModalContextInfo;
  getLookupConfigValidationError?: (inferredType: InferredLookupConfigType) => string | null;
  onLookupChanged?: (lookupConfig: LookupConfigSpec) => void;
  canEditIfArchived?: boolean;
}

type LookupConfigSummaryChipProps = BaseLookupChipProps & (LookupChipPropsEditable | LookupChipPropsStatic);

export default function LookupConfigSummaryChip(props: LookupConfigSummaryChipProps): JSX.Element {
  const {widthCSSClass} = useMemo(() => {
    let widthCSSClass;
    if (props.customWidthPx !== undefined) {
      const fixedWidthClassName = `fixedWidth--${props.customWidthPx}`;
      widthCSSClass = StyleSheet.create({
        [fixedWidthClassName]: {
          width: `${props.customWidthPx}px`,
          whiteSpace: 'nowrap',
        },
      })[fixedWidthClassName];
    }
    return {widthCSSClass};
  }, [props.customWidthPx]);

  const editLookupConfig = (): void => {
    if (props.isEditable) {
      const {onLookupChanged, ...otherProps} = props;
      runLookupConfigModalForLookupConfigSummaryChip({...otherProps, onSubmit: onLookupChanged});
    }
  };

  if (props.lookupStepInfos.length === 0 && props.isEditable) {
    return (
      <Chip
        icon="entity-lookup"
        text="Lookup value"
        size={props.chipSize}
        actionIcon="edit"
        actionTooltipText={EDIT_ACTION_TOOLTIP_TEXT}
        onAction={editLookupConfig}
        onClick={editLookupConfig}
        className={css(styles.lookupChip, widthCSSClass)}
        faded={false}
      />
    );
  }
  assert(
    props.lookupStepInfos.length > 0,
    'Expected non-editable lookup config to have at least one lookup step.'
  );
  const lastLookupStepInfo = props.lookupStepInfos[props.lookupStepInfos.length - 1];
  const icon = lastLookupStepInfo.iconToOverrideDefault ?? getIconForLookupStepInfo(lastLookupStepInfo);
  const {name, isArchived, isDeleted} = getStepDisplayInfo(lastLookupStepInfo);

  const getIsEditable = (): boolean => {
    if (!props.isEditable) {
      return false;
    } else if (isDeleted) {
      return false;
    } else if (isArchived) {
      return props.canEditIfArchived ?? false;
    } else {
      return true;
    }
  };

  const isEditable = getIsEditable();
  const isEditableAndArchived = props.isEditable && isArchived && (props.canEditIfArchived ?? false);

  let actionIconName: BenchlingIconName | undefined;
  let tooltipText: string | undefined;

  if (isEditable && !isEditableAndArchived) {
    actionIconName = 'edit';
    tooltipText = EDIT_ACTION_TOOLTIP_TEXT;
  } else if (isEditableAndArchived) {
    actionIconName = 'undo';
    tooltipText = 'Replace archived value';
  }

  return (
    <Chip
      icon={icon}
      text={name}
      size={props.chipSize}
      negative={isDeleted}
      actionIcon={actionIconName}
      actionTooltipText={tooltipText}
      onAction={isEditable ? editLookupConfig : undefined}
      onClick={isEditable ? editLookupConfig : undefined}
      className={classNames(css(styles.lookupChip, widthCSSClass))}
      faded={isArchived}
      popoverContent={
        <>
          <b>Lookup config</b>
          <div className={css(styles.lookupInfosDescription)}>
            <LookupInfosDescription lookupStepInfos={props.lookupStepInfos} />
          </div>
        </>
      }
    />
  );
}

LookupConfigSummaryChip.defaultProps = {
  chipSize: ChipSize.TABLE_CELL,
};

const styles = StyleSheet.create({
  lookupChip: LOOKUP_CHIP,
  lookupInfosDescription: {
    marginTop: '5px',
  },
});
